import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { ALERT_DURATION } from "../../../../../alert/AlertContextProvider";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetExpensePosition, AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { useSaveAttempted } from "../../../hooks/useSaveAttempted";
import { describeMissingExpenseField } from "../ExpensesView.missingFields";
import { AssetExpensesFormValues, MINIMUM_LIVING_COSTS } from "../ExpensesView.types";
import { fromExpensesResponse, toExpensesRequest } from "../ExpensesView.utils";
import { createExpensesSchema } from "../ExpensesView.validation";

export function useAssetExpenses(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);
  const [livingCostsBelowMinimum, setLivingCostsBelowMinimum] = useState(false);

  const queryKey = [ClientFileQueryKey.AssetInventoryExpenses, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryExpenses({ clientId }),
    enabled: !!clientId,
  });

  const uploadExpenseProofs = async (files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryExpenseProof(
            { clientId },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryExpenseProofs, clientId] });
    return failed;
  };

  const validationSchema = useMemo(() => createExpensesSchema(t), [t]);

  const initialValues = useMemo(() => fromExpensesResponse(data), [data]);

  const formik = useFormikWithDirtyForm<AssetExpensesFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      setLivingCostsBelowMinimum(
        (values.positions[AssetExpensePosition.LivingCosts].debtorShare ?? 0) < MINIMUM_LIVING_COSTS
      );
      const request = toExpensesRequest(values);

      if (JSON.stringify(request) !== JSON.stringify(toExpensesRequest(initialValues))) {
        await clientsApi.saveAssetInventoryExpenses({ clientId, saveAssetExpensesRequest: request });
        await queryClient.invalidateQueries({ queryKey });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.BankruptcyChecklist, clientId] });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
      }

      let uploadFailed = false;
      if (values.expenseProofs.length > 0) {
        const failed = await uploadExpenseProofs(values.expenseProofs);
        await helpers.setFieldValue(nameof<AssetExpensesFormValues>("expenseProofs"), failed);
        uploadFailed = failed.length > 0;
      }

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue(nameof<AssetExpensesFormValues>("documentMoves"), failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.expensesTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);
  const saveAttempted = useSaveAttempted(formik.submitCount);
  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.Expenses,
    formik.values,
    describeMissingExpenseField
  );

  useEffect(() => {
    if (!livingCostsBelowMinimum || formik.isSubmitting) {
      return;
    }
    const timeoutId = setTimeout(() => setLivingCostsBelowMinimum(false), ALERT_DURATION);
    return () => clearTimeout(timeoutId);
  }, [livingCostsBelowMinimum, formik.isSubmitting]);

  return {
    formik,
    maintenance: data?.maintenance,
    summary: data?.summary,
    missingFields,
    saveAttempted,
    livingCostsBelowMinimum,
    onAddMove,
    onCancelMove,
  };
}
